/*
 * Reformation 17.0 header (assets/header.js), ported for sections/header-reformation.liquid.
 * Custom elements are renamed (theme-header-ref, full-menu-ref) so they never collide with
 * the 8.4 theme-header / full-menu. 17.0 opens the drawer through a global OverlayManager
 * that lives in its app.js; ours has none, so a minimal one is provided below.
 *
 * The overlay uses its own body class (header-ref-overlay-open) instead of open-cc: a
 * MutationObserver in layout/theme.liquid strips open-cc whenever no Clerk popup is open.
 */
if (typeof window.OverlayManager === 'undefined') {
  window.OverlayManager = {
    stack: [],
    beforeCloseHandlers: new Map(),
    returnFocus: new WeakMap(),
    bodyClass: 'header-ref-overlay-open',

    emit(name, panel) {
      document.dispatchEvent(new CustomEvent(name, { detail: { panel } }));
    },
    open(panel) {
      if (!panel || this.stack.includes(panel)) return;
      this.stack.push(panel);
      this.returnFocus.set(panel, document.activeElement);
      panel.classList.add('active');
      panel.inert = false;
      document.body.classList.add(this.bodyClass);
      panel.querySelector('.side-panel-close')?.focus();
      this.emit('panel:open', panel);
    },
    close(panel) {
      if (!panel) return;
      const handler = this.beforeCloseHandlers.get(panel);
      if (handler) {
        handler(() => this._close(panel));
      } else {
        this._close(panel);
      }
    },
    _close(panel) {
      const index = this.stack.indexOf(panel);
      if (index > -1) this.stack.splice(index, 1);
      panel.classList.remove('active');
      panel.inert = true;
      if (!this.stack.length) {
        document.body.classList.remove(this.bodyClass);
      }
      const opener = this.returnFocus.get(panel);
      if (opener && document.contains(opener) && (panel.contains(document.activeElement) || document.activeElement === document.body)) {
        opener.focus();
      }
      this.returnFocus.delete(panel);
      this.emit('panel:closed', panel);
    },
    closeLast() {
      const panel = this.stack[this.stack.length - 1];
      if (panel) this.close(panel);
    },
    registerBeforeClose(panel, handler) {
      this.beforeCloseHandlers.set(panel, handler);
    },
    isOpen(panel) {
      return this.stack.includes(panel);
    },
    hasOpen() {
      return this.stack.length > 0;
    },
    trapFocus(e) {
      if (e.key !== 'Tab' || !this.stack.length) return;
      const panel = this.stack[this.stack.length - 1];
      const focusables = Array.from(panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.getClientRects().length > 0);

      if (!focusables.length) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && OverlayManager.hasOpen()) {
      OverlayManager.closeLast();
      return;
    }
    OverlayManager.trapFocus(e);
  });
  // The theme's .click-capture and side-panel-close only know about .side-panel
  // (cart, search, quick view); close the header drawer from them as well.
  document.addEventListener('click', (e) => {
    if (!OverlayManager.hasOpen()) return;
    if (e.target.closest('.click-capture') || e.target.closest('.mobile-menu-drawer--close .side-panel-close')) {
      e.preventDefault();
      OverlayManager.closeLast();
    } else if (e.target.closest('#cart-drawer-toggle, .thb-quick-search')) {
      // Cart drawer / Clerk search open on top: get the menu out of the way.
      while (OverlayManager.hasOpen()) OverlayManager.closeLast();
    }
  });
}
// The theme's app.js exposes no debounce of its own today; fall back to a local
// one. Declared as a const rather than a hoisted `var` guard so this script never
// adds a global binding that a later script's own `debounce` could collide with.
const debounceRef =
  typeof window.debounce === 'function'
    ? window.debounce
    : function (fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      };
/**
 *  @class
 *  @function ThemeHeaderRef
 */
if (!customElements.get('theme-header-ref')) {
  class ThemeHeaderRef extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.full_menu = this.querySelector('.full-menu');

      // The drawer must live outside the header: .header-section creates a
      // stacking context (sticky, z-index 50) that sits below the
      // click-capture (52), so a fixed drawer left inside it could never
      // paint above the dim. It is rendered inside the header section for
      // access to the section's settings/blocks, then teleported to
      // #wrapper next to the other side panels. Stale copies from previous
      // section renders (theme editor) are removed first.
      const wrapper = document.getElementById('wrapper');
      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((el) => {
          if (el !== this.menu) el.remove();
        });
        wrapper.appendChild(this.menu);
      }
      this._scrollTicking = false;
      this._stickyOffset = 0;

      // The drawer opens through OverlayManager, which provides the
      // click-capture, scroll lock, focus trap and Escape handling.
      // The hamburger state is synced from the panel events so every
      // close path (capture click, Escape, close button) resets it.
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.menu)) {
          OverlayManager.close(this.menu);
        } else {
          OverlayManager.open(this.menu);
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });
      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.add('active');
          // Measure the header again as the menu opens. Its position only
          // matters while the drawer is on screen, and anything that shifted
          // the layout since load (the announcement bar marquee collapsing,
          // late fonts, a dismissed bar) would otherwise leave the drawer
          // sitting in a gap below the header.
          this.setHeaderBottom();
        }
      });
      document.addEventListener('panel:closed', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.remove('active');
          this.toggle.removeAttribute('open');
        }
      });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounceRef(() => {
        this._cacheStickyOffset();
        this.setHeaderBottom();
      }, 100));
      // Topmenuens layout skiftes i samme frame som størrelsesændringen (ikke
      // debounced), så menuen aldrig står et øjeblik oven i ikonerne. Trækkes
      // vinduet op over mobil-bredden med skuffen åben, lukkes den, da
      // hamburgeren så er væk (medmindre skuffen også bruges på desktop).
      this._onResizeMenu = () => {
        this.fitFullMenu();
        if (OverlayManager.isOpen(this.menu) && !this.classList.contains('header--drawer-menu') && window.matchMedia('(min-width: 1068px)').matches) {
          OverlayManager.close(this.menu);
        }
      };
      window.addEventListener('resize', this._onResizeMenu);

      this._initHideOnScroll();

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHideOnScroll();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this.setHeaderBottom();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));

      // Those vars are otherwise only refreshed on scroll, so they keep
      // whatever the layout was at load. The announcement bar marquee
      // collapses shortly after load (and late fonts can resize the header),
      // which left them measuring a taller header than the one on screen —
      // the mobile menu then opened a gap below it. Re-measure whenever the
      // header or the bar actually changes geometry. The bar is observed as
      // well because its collapse moves the header without resizing it.
      if ('ResizeObserver' in window) {
        this._headerResizeObserver = new ResizeObserver(() => {
          // Fanger breddeændringer uden resize-event (fx når sidens scrollbar
          // kommer til). Målingen venter til næste frame, fordi et skift af
          // layout ændrer headerens højde, og det må ikke ske inde i
          // ResizeObserver-callbacken (giver "ResizeObserver loop"-fejl).
          this.queueFitFullMenu();
          this.setHeaderOffset();
          this.setHeaderHeight();
          this.setHeaderBottom();
        });
        this._headerResizeObserver.observe(this);
        const a_bar_section = document.querySelector('.announcement-bar-section');
        if (a_bar_section) {
          this._headerResizeObserver.observe(a_bar_section);
        }
      }

      // Topmenuens layout måles først, når skrifterne er hentet: med
      // reserveskriften er menupunkterne bredere, og headeren ville skifte
      // layout to gange under indlæsning. Indtil da gælder CSS'ens standard
      // (menu ved siden af logoet).
      this._menuFontsReady = false;
      this.getBoundingClientRect(); // start indlæsning af skrifter brugt i headeren
      document.fonts.ready.then(() => {
        this._menuFontsReady = true;
        this.fitFullMenu();
      });

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Det store søgefelt (header-ref-search-bar) åbner Clerks omnisearch
      // gennem søgeikonet, som Clerk er bundet til (data-trigger-element).
      this.querySelectorAll('[data-header-search-bar]').forEach((bar) => {
        bar.addEventListener('click', (e) => {
          const trigger = document.getElementById('omnisearch');
          if (!trigger) return;
          e.preventDefault();
          trigger.click();
        });
      });

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

      // Billedkort-slideren i skuffen kan trækkes med musen.
      this.menu.querySelectorAll('.mobile-menu-cards__track').forEach((track) => this.initCardDrag(track));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    // Topmenuen (style2) står på én linje ved siden af logoet, når der er plads,
    // og ellers på sin egen linje under logo og ikoner (header--menu-stacked).
    // Hamburger og skuffemenu bruges kun under 1068px, se header-ref.css.
    // Målingen giver samme resultat i begge layouts (punkternes bredde, logoet og
    // ikonerne ændrer sig ikke), så headeren kan ikke skifte frem og tilbage, og
    // flere eller længere menupunkter håndteres automatisk. Er der heller ikke
    // plads på menuens egen linje, ombrydes punkterne (header--menu-wrap), så
    // intet punkt skjules. Gælder style2 og style1 (logo til venstre, menu i
    // midten); i style1 regnes der med luft på begge sider af menuen.
    // Med indstillingen "Menu altid på egen linje under logoet"
    // (header--menu-below, sat i header-reformation.liquid) står menuen altid
    // på sin egen linje; målingen afgør så kun, om punkterne skal ombrydes.
    fitFullMenu() {
      const centered = this.classList.contains('style1');
      if (!this.full_menu || !(centered || this.classList.contains('style2')) || this.classList.contains('header--drawer-menu')) {
        return;
      }
      if (!window.matchMedia('(min-width: 1068px)').matches) {
        this.setMenuLayout(false, false);
        return;
      }
      if (!this._menuFontsReady) {
        return;
      }
      const columns = this.querySelector('.row > .columns');
      const list = this.full_menu.querySelector('.thb-full-menu');
      const logo = this.querySelector('.logolink');
      const secondary = this.querySelector('.thb-secondary-area');
      if (!columns || !list || !list.children.length || !secondary) {
        return;
      }
      const columns_style = getComputedStyle(columns);
      const width = columns.clientWidth - parseFloat(columns_style.paddingLeft) - parseFloat(columns_style.paddingRight);
      // Menupunkternes samlede bredde inkl. deres afstand (padding) og margener.
      let items = 0;
      for (const item of list.children) {
        const item_style = getComputedStyle(item);
        items += item.getBoundingClientRect().width + (parseFloat(item_style.marginLeft) || 0) + (parseFloat(item_style.marginRight) || 0);
      }
      let inline_width = items + secondary.getBoundingClientRect().width;
      if (logo) {
        const gap = parseFloat(getComputedStyle(this).getPropertyValue('--header-menu-gap')) || 25;
        inline_width += logo.getBoundingClientRect().width + (centered ? gap * 2 : gap);
      }
      // På egen linje trækkes listen ud over første og sidste punkts afstand.
      const spacing = parseFloat(getComputedStyle(list.firstElementChild).paddingLeft) || 0;
      // 4px margen mod afrunding, så menuen aldrig rører ikonerne.
      const stacked = this.classList.contains('header--menu-below') || inline_width > width - 4;
      this.setMenuLayout(stacked, stacked && items > width + spacing * 2);
    }
    queueFitFullMenu() {
      if (this._fitFrame) {
        return;
      }
      this._fitFrame = requestAnimationFrame(() => {
        this._fitFrame = null;
        this.fitFullMenu();
      });
    }
    setMenuLayout(stacked, wrap) {
      this.classList.toggle('header--menu-stacked', stacked);
      this.classList.toggle('header--menu-wrap', wrap);
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    // "Hide on scroll down, show on scroll up" (sticky_behavior). The header
    // slides away once the page is scrolled past the announcement bar, the
    // header itself and a small buffer, and comes back on the first upward
    // scroll. While the footer is in view it is held hidden so the logo is
    // only on screen once; scrolling back up past the footer releases it.
    // Nothing changes while a drawer, the cart, search or a mega menu is
    // open, and pages too short to scroll meaningfully never hide it.
    _initHideOnScroll() {
      if (!this.classList.contains('header-hide-on-scroll--active')) return;
      this._lastScrollY = window.scrollY;
      this._footerInView = false;
      const footer = document.querySelector('.section-footer') || document.getElementById('shopify-section-footer');
      if (footer && 'IntersectionObserver' in window) {
        this._footerObserver = new IntersectionObserver((entries) => {
          this._footerInView = entries.some((entry) => entry.isIntersecting);
          this.setHideOnScroll(true);
        }, { rootMargin: '0px 0px 120px 0px' });
        this._footerObserver.observe(footer);
      }
    }
    setHideOnScroll(fromObserver) {
      if (!this.classList.contains('header-hide-on-scroll--active')) return;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      // Clamp so rubber-banding at either end never reads as a direction change.
      const y = Math.min(Math.max(window.scrollY, 0), Math.max(maxScroll, 0));
      const delta = y - (this._lastScrollY || 0);
      this._lastScrollY = y;

      const body = document.body.classList;
      if (body.contains('header-ref-overlay-open') || body.contains('open-cart') || body.contains('open-cc') || body.contains('open-quick-view')) return;
      if (this.querySelector('.menu-item-has-children:hover')) return;

      const threshold = (this._stickyOffset || 0) + this.clientHeight + 120;
      if (maxScroll < threshold + 200 || y <= threshold) {
        this.classList.remove('header--scrolled-away');
        return;
      }
      if (this._footerInView) {
        this.classList.add('header--scrolled-away');
        return;
      }
      if (fromObserver) return;
      if (delta > 4) {
        this.classList.add('header--scrolled-away');
      } else if (delta < -2) {
        this.classList.remove('header--scrolled-away');
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    disconnectedCallback() {
      // The theme editor re-renders the section — without this the old
      // observer keeps firing against a detached header.
      if (this._headerResizeObserver) {
        this._headerResizeObserver.disconnect();
        this._headerResizeObserver = null;
      }
      if (this._fitFrame) {
        cancelAnimationFrame(this._fitFrame);
        this._fitFrame = null;
      }
      window.removeEventListener('resize', this._onResizeMenu);
      if (this._footerObserver) {
        this._footerObserver.disconnect();
        this._footerObserver = null;
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    // The header's real bottom edge. --header-offset + --header-height only
    // approximates it: clientHeight drops the border, and neither accounts
    // for a transparent header being taken out of flow. Measure it directly
    // so the mobile menu can sit flush under the header. Clamped at 0 so a
    // non-sticky header scrolling away puts the menu at the viewport top.
    setHeaderBottom() {
      let b = this.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--header-bottom', Math.max(0, b) + 'px');
    }
    // Billedkort-slideren (header-ref-mobile-menu-cards) er en almindelig
    // scroll-container, som touch og trackpad kan swipe i, men en mus kan
    // ikke trække i den. Her flyttes den med musen: scroll-snap slås fra,
    // mens der trækkes (ellers hopper den tilbage), og klikket på kortet
    // under et træk annulleres, så man ikke åbner kollektionen ved et uheld.
    // Touch og pen bruger stadig den indbyggede scroll.
    initCardDrag(track) {
      let startX = 0;
      let startScroll = 0;
      let dragged = false;
      let pointerId = null;

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        dragged = false;
      });
      track.addEventListener('pointermove', (e) => {
        if (e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        if (!dragged && Math.abs(dx) < 5) return;
        if (!dragged) {
          dragged = true;
          track.setPointerCapture(pointerId);
          track.classList.add('is-dragging');
        }
        track.scrollLeft = startScroll - dx;
      });
      const end = (e) => {
        if (e.pointerId !== pointerId) return;
        pointerId = null;
        if (!dragged) return;
        track.classList.remove('is-dragging');
        // Lad scroll-snap lægge det nærmeste kort på plads igen.
        const left = track.scrollLeft;
        track.scrollLeft = left + 1;
        track.scrollLeft = left;
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      track.addEventListener('click', (e) => {
        if (dragged) {
          e.preventDefault();
          e.stopPropagation();
          dragged = false;
        }
      }, true);
      // Træk ikke billeder/links som filer.
      track.addEventListener('dragstart', (e) => e.preventDefault());
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header-ref', ThemeHeaderRef);
}
/**
 *  @class
 *  @function FullMenuRef
 */
if (!customElements.get('full-menu-ref')) {
  class FullMenuRef extends HTMLElement {
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounceRef(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu-ref', FullMenuRef);
}
